import * as React from "react";
import {
  QuestionCircle24Regular,
  Clipboard24Regular,
  Note24Regular,
  ShieldCheckmark24Regular,
  BookInformation24Regular,
  ArrowSync24Regular,
  DocumentText24Regular,
  Person24Regular,
  Handshake24Regular,
  PersonFeedback24Regular,
  DocumentFolder24Regular,
} from "@fluentui/react-icons";

export interface DiscussionTypeOption {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType;
  color: string;
}

/**
 * Main discussion types (top level)
 */
export const MAIN_DISCUSSION_TYPES: DiscussionTypeOption[] = [
  {
    id: "client_query",
    name: "Client Query",
    description: "Questions from clients",
    icon: QuestionCircle24Regular,
    color: "#F7630C", // Orange
  },
  {
    id: "review_point",
    name: "Review Point",
    description: "Items identified during review",
    icon: Clipboard24Regular,
    color: "#0078D4", // Blue
  },
  {
    id: "notes",
    name: "Notes",
    description: "Internal notes and documentation",
    icon: Note24Regular,
    color: "#008080", // Teal
  },
];

/**
 * Tags for Review Point discussions (multi-select)
 */
export const REVIEW_POINT_TAGS: DiscussionTypeOption[] = [
  {
    id: "quality_control",
    name: "Quality Control",
    description: "Report accuracy issues",
    icon: ShieldCheckmark24Regular,
    color: "#D13438", // Red
  },
  {
    id: "learning_opportunity",
    name: "Learning Opportunity",
    description: "Share knowledge",
    icon: BookInformation24Regular,
    color: "#107C10", // Green
  },
  {
    id: "process_improvement",
    name: "Process Improvement",
    description: "Suggest optimizations",
    icon: ArrowSync24Regular,
    color: "#0078D4", // Blue
  },
  {
    id: "documentation",
    name: "Documentation",
    description: "Update procedures",
    icon: DocumentText24Regular,
    color: "#5C2D91", // Purple
  },
];

/**
 * Tags for Notes discussions (multi-select)
 */
export const NOTES_TAGS: DiscussionTypeOption[] = [
  {
    id: "notes_for_client",
    name: "For Client",
    description: "Notes to share with client",
    icon: Person24Regular,
    color: "#0078D4", // Blue
  },
  {
    id: "notes_for_partner",
    name: "For Partner",
    description: "Notes for partner review",
    icon: Handshake24Regular,
    color: "#8764B8", // Purple
  },
  {
    id: "notes_for_quality",
    name: "For Quality Review",
    description: "Notes for quality reviewer attention",
    icon: PersonFeedback24Regular,
    color: "#CA5010", // Orange
  },
  {
    id: "notes_for_file",
    name: "For File",
    description: "Internal file documentation",
    icon: DocumentFolder24Regular,
    color: "#498205", // Green
  },
];

/**
 * Get tags for a given main discussion type
 * @param mainType - The main discussion type ID
 * @returns Array of tag options, or null if type doesn't support tags
 */
export function getTagsForMainType(
  mainType: string
): DiscussionTypeOption[] | null {
  switch (mainType) {
    case "review_point":
      return REVIEW_POINT_TAGS;
    case "notes":
      return NOTES_TAGS;
    case "client_query":
      return null; // No tags
    default:
      return null;
  }
}

/**
 * Check if a main type supports tag selection (multi-select)
 * @param mainType - The main discussion type ID
 * @returns True if tags are supported
 */
export function requiresTags(mainType: string): boolean {
  return mainType === "review_point" || mainType === "notes";
}

/**
 * Get all type options (main types and tags combined)
 * Useful for lookups and display
 */
export const ALL_DISCUSSION_TYPES = [
  ...MAIN_DISCUSSION_TYPES,
  ...REVIEW_POINT_TAGS,
  ...NOTES_TAGS,
];

/**
 * Find a type option by ID across all types
 * @param typeId - The type ID to find
 * @returns The type option or undefined
 */
export function findTypeById(
  typeId: string
): DiscussionTypeOption | undefined {
  return ALL_DISCUSSION_TYPES.find((t) => t.id === typeId);
}
